/**
 * Archetype labels + descriptions — single source of truth for the five
 * spin-out archetypes rendered across Radar and the candidate detail page.
 *
 * `short` stays narrow for table cells / summary chips; `full` expands for
 * filter rows and subtitle contexts; `description` is the one-liner that
 * explains the signal, shown under `full` in the Radar filter popover.
 */
import type { SpinoutArchetype } from './api/spinoutApi';

export interface ArchetypeMeta {
  short: string;
  full: string;
  description: string;
}

export const ARCHETYPE_META: Record<SpinoutArchetype, ArchetypeMeta> = {
  solo_founder: {
    short: 'Solo founder',
    full: 'Solo founder spin-out',
    description: 'One founder leaves a parent company and incorporates a new entity.',
  },
  co_founder: {
    short: 'Co-founder',
    full: 'Co-founder spin-out',
    description: 'Two or more people from a parent leave and co-found a new vehicle together.',
  },
  team_liftout: {
    short: 'Team lift-out',
    full: 'Team lift-out',
    description: 'Three or more people from a parent company move into the new entity.',
  },
  psc_migration: {
    short: 'Ownership move',
    full: 'Ownership migration (PSC)',
    description: 'A person with significant control transfers their stake from parent to new entity.',
  },
  corporate_carveout: {
    short: 'Carve-out',
    full: 'Corporate carve-out',
    description: 'A division or subsidiary is spun out as an independent company.',
  },
  related_entity: {
    short: 'Related entity',
    full: 'Related entity',
    description: 'Company names share a brand prefix — likely a subsidiary, parallel fund, or sister vehicle rather than a true spin-out.',
  },
};

export const ARCHETYPE_SHORT: Record<SpinoutArchetype | 'all', string> = {
  all: 'All',
  solo_founder: ARCHETYPE_META.solo_founder.short,
  co_founder: ARCHETYPE_META.co_founder.short,
  team_liftout: ARCHETYPE_META.team_liftout.short,
  psc_migration: ARCHETYPE_META.psc_migration.short,
  corporate_carveout: ARCHETYPE_META.corporate_carveout.short,
  related_entity: ARCHETYPE_META.related_entity.short,
};

export const ARCHETYPE_ORDER: (SpinoutArchetype | 'all')[] = [
  'all', 'solo_founder', 'co_founder', 'team_liftout', 'psc_migration', 'corporate_carveout',
];

export const ARCHETYPE_COLOR: Record<SpinoutArchetype, string> = {
  solo_founder: '#F5631A',
  co_founder: '#2563EB',
  team_liftout: '#0A9B66',
  psc_migration: '#D4940A',
  corporate_carveout: '#7A756C',
  related_entity: '#9B9B9B',
};

export const ARCHETYPE_FILTER_LABEL: Record<SpinoutArchetype | 'all', string> = {
  all: 'All',
  solo_founder: 'Solo',
  co_founder: 'Co-founder',
  team_liftout: 'Team',
  psc_migration: 'PSC',
  corporate_carveout: 'Carve-out',
  related_entity: 'Related',
};
